<!-- 添加会议室 -->
<template>
    <div class="page">
        <van-nav-bar
            :title="pageTitle"
            left-arrow
            fixed
            placeholder
            safe-area-inset-top
            @click-left="goBack"
        />

        <div class="content">
            <div class="card">
                <div
                    class="field-row field-row-input field-row--picker"
                    role="button"
                    tabindex="0"
                    @click="openProjectSitePicker"
                >
                    <span class="field-label">项目名称</span>
                    <div class="field-value-wrap">
                        <span
                            class="field-input field-input--picker"
                            :class="{ 'is-placeholder': !form.buildingName }"
                        >{{ form.buildingName || '请选择项目名称' }}</span>
                        <span class="field-arrow" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <div
                    class="field-row field-row-input field-row--picker"
                    role="button"
                    tabindex="0"
                    @click="openFloorPicker"
                >
                    <span class="field-label">所属楼层</span>
                    <div class="field-value-wrap">
                        <span
                            class="field-input field-input--picker"
                            :class="{ 'is-placeholder': !form.floor }"
                        >{{ form.floor || '请选择所属楼层' }}</span>
                        <span class="field-arrow" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                    </div>
                </div>
                <div class="field-row field-row-input field-row--name">
                    <span class="field-label">会议室名称</span>
                    <input
                        v-model.trim="form.name"
                        class="field-input"
                        type="text"
                        maxlength="50"
                        placeholder="请输入会议室名称"
                    />
                </div>
                <div class="field-row field-row-input field-row--name">
                    <span class="field-label">会议室设施</span>
                    <input
                        v-model.trim="form.roomEquipt"
                        class="field-input"
                        type="text"
                        maxlength="200"
                        placeholder="请输入设施(用;隔开)"
                    />
                </div>
                <div class="card-title card-title--image">会议室图片</div>
                <div class="upload-section" :class="{ 'upload-section--has-preview': previewUrl }">
                    <div v-if="previewUrl" class="preview-wrap">
                        <img class="preview-img" :src="previewUrl" alt="会议室图片" />
                    </div>
                    <p v-if="previewUrl" class="upload-tip upload-tip--compact">
                        仅支持上传 1 张图片
                    </p>
                    <input
                        ref="imageInput"
                        type="file"
                        accept="image/*"
                        class="upload-input-hidden"
                        @change="onImageFileChange"
                    />
                    <button
                        class="upload-btn"
                        type="button"
                        :disabled="uploading"
                        @click="onUploadImageClick"
                    >
                        {{ previewUrl ? '重新上传' : '上传图片' }}
                    </button>
                    <p v-if="!previewUrl" class="upload-tip">仅支持上传 1 张图片</p>
                </div>
            </div>

            <div class="card">
                <div class="access-sn-block">
                    <p
                        class="access-sn-tip"
                        role="button"
                        tabindex="0"
                        @click="goSnCodeDoc"
                    >获取门禁SN码</p>
                    <div class="field-row field-row-input field-row--access-sn">
                        <span class="field-label">门禁SN码</span>
                        <input
                            v-model.trim="form.deviceSn"
                            class="field-input"
                            type="text"
                            maxlength="64"
                            placeholder="选填，区分大小写"
                        />
                    </div>
                </div>
                <div class="field-row field-row-input field-row--readonly">
                    <span class="field-label">开放时间</span>
                    <span class="field-input">{{ openTimeText }}</span>
                </div>
                <div class="field-row field-row-input">
                    <span class="field-label">容纳人数</span>
                    <input
                        v-model.trim="form.capacity"
                        class="field-input"
                        type="number"
                        inputmode="numeric"
                        min="1"
                        max="999"
                        placeholder="请输入容纳人数"
                    />
                </div>
            </div>

            <div class="card">
                <div class="coord-title-row">
                    <p class="card-title card-title--coord">会议室所在位置的经纬度</p>
                    <button
                        type="button"
                        class="coord-fetch-btn"
                        @click="getUserLocation"
                    >获取当前位置经纬度</button>
                </div>
                <div class="field-row field-row-input field-row--name">
                    <span class="field-label">经度(longitude)</span>
                    <input
                        class="field-input"
                        type="text"
                        inputmode="decimal"
                        maxlength="20"
                        placeholder="请输入经度"
                        :value="longitude"
                        @input="onCoordInput('longitude', $event)"
                    />
                </div>
                <div class="field-row field-row-input field-row--name">
                    <span class="field-label">纬度(latitude)</span>
                    <input
                        class="field-input"
                        type="text"
                        inputmode="decimal"
                        maxlength="20"
                        placeholder="请输入纬度"
                        :value="latitude"
                        @input="onCoordInput('latitude', $event)"
                    />
                </div>
            </div>

            <div class="card">
                <div class="coord-title-row">
                    <p class="card-title card-title--coord">添加其他管理员</p>
                    <button
                        type="button"
                        class="coord-fetch-btn"
                        @click="addAdminSlot"
                    >添加</button>
                </div>
                <div
                    v-for="(slot, index) in adminSlots"
                    :key="'admin-slot-' + index"
                    class="field-row field-row-input field-row--picker field-row--admin"
                    role="button"
                    tabindex="0"
                    @click="openAdminPicker(index)"
                >
                    <div class="admin-label-wrap">
                        <button
                            type="button"
                            class="admin-remove-btn"
                            aria-label="删除管理员"
                            @click.stop="removeAdminSlot(index)"
                        >
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                <circle cx="12" cy="12" r="9" stroke="currentColor" stroke-width="2"/>
                                <path d="M7 12h10" stroke="currentColor" stroke-width="3" stroke-linecap="round"/>
                            </svg>
                        </button>
                        <span class="field-label">管理员{{ index + 1 }}</span>
                    </div>
                    <div class="field-value-wrap">
                        <span
                            class="field-input field-input--picker"
                            :class="{ 'is-placeholder': !slot.label }"
                        >{{ slot.label || '请选择管理员' }}</span>
                        <span class="field-arrow" aria-hidden="true">
                            <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M9 6l6 6-6 6" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </span>
                    </div>
                </div>
            </div>

            <button
                class="submit-btn"
                type="button"
                :disabled="submitting || uploading"
                @click="handleSubmit"
            >保存</button>
        </div>

        <van-popup v-model="showProjectSitePicker" position="bottom" round>
            <van-picker
                :key="projectSitePickerKey"
                title="项目名称"
                show-toolbar
                value-key="label"
                :columns="projectSiteColumns"
                :default-index="projectSitePickerIndex"
                @confirm="onConfirmProjectSite"
                @cancel="showProjectSitePicker = false"
            />
        </van-popup>

        <van-popup v-model="showFloorPicker" position="bottom" round>
            <van-picker
                :key="floorPickerKey"
                title="所属楼层"
                show-toolbar
                value-key="label"
                :columns="floorColumns"
                :default-index="floorPickerIndex"
                @confirm="onConfirmFloor"
                @cancel="showFloorPicker = false"
            />
        </van-popup>

        <van-popup v-model="showAdminPicker" position="bottom" round>
            <van-picker
                :key="adminPickerKey"
                title="选择管理员"
                show-toolbar
                value-key="label"
                :columns="adminPickerColumns"
                :default-index="adminPickerIndex"
                @confirm="onConfirmAdmin"
                @cancel="showAdminPicker = false"
            />
        </van-popup>

    </div>
</template>

<script>

import MonitorKeyboard from '../../tools/MonitorKeyboard.js'
import {
    saveMeetingRoomRequest,
    uploadFileRequest,
    getBuildingNamesRequest,
    getBuildingFloorsRequest,
    getOtherNormalAdminRequest
} from '../../request/api'
import { isUnauthorizedError } from '@/tools/utils'
import { Toast } from 'vant'

const MEETING_ROOM_EDIT_KEY = 'meetingRoomEdit'
const IMAGE_BASE = 'https://meetingroom.shdspf.cn'

export default {
    name: 'AddMeetingRoom',
    data() {
        return {
            roomId: null,
            localPreviewUrl: '',
            // 经度（文本，提交时 parseFloat）
            longitude: '',
            // 纬度（文本，提交时 parseFloat）
            latitude: '',
            form: {
                buildingName: '',
                projectSiteId: '',
                floor: '',
                floorId: '',
                name: '',
                roomEquipt: '',
                deviceSn: '',
                capacity: '',
            },
            projectSiteOptions: [],
            floorOptions: [],
            otherNormalAdmins: [],
            adminOptions: [],
            adminSlots: [{ label: '', value: '', phone: '', name: '' }],
            showProjectSitePicker: false,
            projectSiteColumns: [],
            projectSitePickerIndex: 0,
            projectSitePickerKey: 0,
            showFloorPicker: false,
            floorColumns: [],
            floorPickerIndex: 0,
            floorPickerKey: 0,
            showAdminPicker: false,
            adminPickerColumns: [],
            adminPickerIndex: 0,
            adminPickerKey: 0,
            adminPickerSlotIndex: 0,
            submitting: false,
            uploading: false,
            uploadedCover: '',
            /** 与当前添加/编辑入口绑定，避免从 snCodeDoc 返回时重复 reset */
            addRoomRouteKey: '',
        }
    },
    created() {
        this.syncProjectSiteColumns()
        this.syncFloorColumns()
        this.initPageFromRoute()
    },
    watch: {
        '$route.fullPath'() {
            if (this.$route.name === 'addMeetingRoom') {
                this.initPageFromRoute()
            }
        },
    },
    beforeRouteLeave(to, from, next) {
        if (to.name === 'meetingRoomManage') {
            this.addRoomRouteKey = ''
        }
        next()
    },
    computed: {
        isEditMode() {
            return this.roomId != null && this.roomId !== ''
        },
        pageTitle() {
            return this.isEditMode ? '编辑会议室' : '添加会议室'
        },
        previewUrl() {
            if (this.localPreviewUrl) return this.localPreviewUrl
            return this.resolveCoverUrl(this.uploadedCover)
        },
        openTimeText() {
            return '09:00-18:00'
        },
    },
    mounted() {
        this.getKeyboardState()
    },
    beforeDestroy() {
        if (this.monitorKeyboard) {
            this.monitorKeyboard.onEnd();
        }
        this.revokeLocalPreview()
    },
    methods: {
        getKeyboardState(){
            this.monitorKeyboard = new MonitorKeyboard();
            this.monitorKeyboard.onStart();

            this.monitorKeyboard.onShow(() => {
                this.isShowText = false;
            })

            this.monitorKeyboard.onHidden(() => {
                this.isShowText = true;
            })
        },

        getUserLocation() {
            console.log('--->获取经纬度');
            setTimeout(() => {
                wx.getLocation({
                    type: 'wgs84',
                    success: (res) => {
                        console.log('获取地理位置成功--->', res)
                        const lng = parseFloat(res.longitude)
                        const lat = parseFloat(res.latitude)
                        this.longitude = Number.isNaN(lng) ? '' : String(lng)
                        this.latitude = Number.isNaN(lat) ? '' : String(lat)
                    },
                    fail: () => {
                        this.$toast && this.$toast('地理位置获取失败')
                    },
                })
            }, 500)
        },

        goBack() {
            this.$router.back()
        },
        goSnCodeDoc() {
            this.$router.push({ name: 'snCodeDoc' })
        },
        getDefaultForm() {
            return {
                buildingName: '',
                projectSiteId: '',
                floor: '',
                floorId: '',
                name: '',
                roomEquipt: '',
                deviceSn: '',
                capacity: '',
            }
        },
        resetFormState() {
            this.revokeLocalPreview()
            this.roomId = null
            this.form = this.getDefaultForm()
            this.uploadedCover = ''
            this.longitude = ''
            this.latitude = ''
            this.uploading = false
            this.submitting = false
            this.floorOptions = []
            this.adminSlots = [{ label: '', value: '', phone: '', name: '' }]
            this.syncFloorColumns()
        },
        syncProjectSiteColumns() {
            this.projectSiteColumns = this.projectSiteOptions.length
                ? [{ values: this.projectSiteOptions }]
                : []
        },
        syncFloorColumns() {
            this.floorColumns = this.floorOptions.length
                ? [{ values: this.floorOptions }]
                : []
        },
        getAdminDisplayLabel(item) {
            if (!item || typeof item !== 'object') return ''
            const name = String(
                item.name || item.userName || item.realName || item.adminName || ''
            ).trim()
            const phone = String(
                item.phone || item.mobile || item.phoneNumber || item.tel || ''
            ).trim()
            if (name && phone) return `${name} ${phone}`
            return name || phone
        },
        getAdminOptionValue(item) {
            if (!item || typeof item !== 'object') return ''
            const id = item.id != null ? item.id : item.adminId != null ? item.adminId : item.userId
            if (id != null && String(id).trim() !== '') return String(id)
            const phone = String(
                item.phone || item.mobile || item.phoneNumber || item.tel || ''
            ).trim()
            return phone
        },
        normalizeAdminOptions(list) {
            if (!Array.isArray(list)) return []
            return list
                .map((item) => {
                    if (item == null || typeof item !== 'object') return null
                    const label = this.getAdminDisplayLabel(item)
                    const value = this.getAdminOptionValue(item)
                    if (!label || !value) return null
                    return {
                        label,
                        value,
                        name: String(
                            item.name || item.userName || item.realName || item.adminName || ''
                        ).trim(),
                        phone: String(
                            item.phone || item.mobile || item.phoneNumber || item.tel || ''
                        ).trim(),
                        source: item,
                    }
                })
                .filter(Boolean)
        },
        getAvailableAdminOptions(slotIndex) {
            const selectedValues = this.adminSlots
                .map((slot, idx) => (idx === slotIndex ? '' : (slot && slot.value) || ''))
                .filter(Boolean)
            return this.adminOptions.filter((opt) => !selectedValues.includes(opt.value))
        },
        normalizePickerOptions(list, textKeys) {
            if (!Array.isArray(list)) return []
            const keys = textKeys || ['buildingName', 'name', 'label', 'value', 'floor']
            return list
                .map((item) => {
                    if (item == null) return null
                    if (typeof item === 'string') {
                        const text = item.trim()
                        return text ? { label: text, value: text } : null
                    }
                    if (typeof item === 'object') {
                        let text = ''
                        for (let i = 0; i < keys.length; i += 1) {
                            const v = item[keys[i]]
                            if (v != null && String(v).trim()) {
                                text = String(v).trim()
                                break
                            }
                        }
                        return text ? { label: text, value: text } : null
                    }
                    return null
                })
                .filter(Boolean)
        },
        extractResponseList(res) {
            if (Array.isArray(res)) return res
            if (res && Array.isArray(res.data)) return res.data
            if (res && res.data && Array.isArray(res.data.list)) return res.data.list
            if (res && Array.isArray(res.list)) return res.list
            return []
        },
        loadBuildingNames() {
            const params = {}
            return getBuildingNamesRequest(params)
                .then((res) => {
                    this.projectSiteOptions = this.normalizePickerOptions(
                        this.extractResponseList(res),
                        ['buildingName', 'name']
                    )
                    this.syncProjectSiteColumns()
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.projectSiteOptions = []
                    this.syncProjectSiteColumns()
                    const msg = (err && (err.message || err.msg)) || '获取项目名称失败'
                    this.$toast && this.$toast(msg)
                })
        },
        loadOtherNormalAdmins() {
            return getOtherNormalAdminRequest({})
                .then((res) => {
                    this.otherNormalAdmins = this.extractResponseList(res)
                    this.adminOptions = this.normalizeAdminOptions(this.otherNormalAdmins)
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.otherNormalAdmins = []
                    this.adminOptions = []
                    const msg = (err && (err.message || err.msg)) || '获取管理员信息失败'
                    this.$toast && this.$toast(msg)
                })
        },
        addAdminSlot() {
            if (!this.adminOptions.length) {
                this.$toast && this.$toast('暂无可选管理员')
                return
            }
            if (this.adminSlots.length >= this.adminOptions.length) {
                this.$toast && this.$toast('已达到可选管理员上限')
                return
            }
            this.adminSlots.push({ label: '', value: '', phone: '', name: '' })
        },
        removeAdminSlot(index) {
            if (this.adminSlots.length <= 1) {
                this.$set(this.adminSlots, 0, {
                    label: '',
                    value: '',
                    phone: '',
                    name: '',
                })
                return
            }
            this.adminSlots.splice(index, 1)
        },
        openAdminPicker(index) {
            const available = this.getAvailableAdminOptions(index)
            if (!available.length) {
                this.$toast && this.$toast('暂无可选管理员')
                return
            }
            const current = this.adminSlots[index]
            const idx = available.findIndex(
                (item) => current && item.value === current.value
            )
            this.adminPickerSlotIndex = index
            this.adminPickerIndex = idx >= 0 ? idx : 0
            this.adminPickerColumns = [{ values: available }]
            this.adminPickerKey += 1
            this.showAdminPicker = true
        },
        onConfirmAdmin(values) {
            const selected = Array.isArray(values) ? values[0] : values
            const index = this.adminPickerSlotIndex
            if (!this.adminSlots[index]) {
                this.showAdminPicker = false
                return
            }
            if (selected && typeof selected === 'object') {
                this.$set(this.adminSlots, index, {
                    label: selected.label || '',
                    value: selected.value || '',
                    name: selected.name || '',
                    phone: selected.phone || '',
                })
            }
            this.showAdminPicker = false
        },
        loadBuildingFloors(buildingName) {
            const name = String(buildingName || '').trim()
            if (!name) {
                this.floorOptions = []
                this.syncFloorColumns()
                return Promise.resolve()
            }
            return getBuildingFloorsRequest({ buildingName: name })
                .then((res) => {
                    this.floorOptions = this.normalizePickerOptions(
                        this.extractResponseList(res),
                        ['floor', 'floorName', 'name']
                    )
                    this.syncFloorColumns()
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.floorOptions = []
                    this.syncFloorColumns()
                    const msg = (err && (err.message || err.msg)) || '获取楼层失败'
                    this.$toast && this.$toast(msg)
                })
        },
        resolveCoverUrl(url) {
            const raw = String(url || '').trim()
            if (!raw) return ''
            if (/^https?:\/\//i.test(raw)) return raw
            if (raw.startsWith('//')) return `https:${raw}`
            const path = raw.startsWith('/') ? raw : `/${raw}`
            return `${IMAGE_BASE}${path}`
        },
        readMeetingRoomEditCache(expectedId) {
            if (expectedId == null || expectedId === '') return null
            try {
                const cached = JSON.parse(
                    sessionStorage.getItem(MEETING_ROOM_EDIT_KEY) || 'null'
                )
                console.log('cached--->', cached);
                if (!cached || typeof cached !== 'object') return null
                if (String(cached.roomId) !== String(expectedId)) return null
                return cached
            } catch (e) {
                return null
            }
        },
        applyCoordFromCache(coord) {
            if (!coord || typeof coord !== 'object') return
            const lng = parseFloat(coord.lng != null ? coord.lng : coord.longitude)
            const lat = parseFloat(coord.lat != null ? coord.lat : coord.latitude)
            // 接口返回 0 视为无有效坐标，不回显
            if (!Number.isNaN(lng) && lng !== 0) this.longitude = String(lng)
            if (!Number.isNaN(lat) && lat !== 0) this.latitude = String(lat)
        },
        filterCoordInput(raw) {
            if (raw == null || raw === '') return ''
            let s = String(raw).replace(/[^\d.+\-]/g, '')
            let sign = ''
            if (s.startsWith('+') || s.startsWith('-')) {
                sign = s.charAt(0)
                s = s.slice(1)
            }
            s = s.replace(/[+\-]/g, '')
            const dotIndex = s.indexOf('.')
            if (dotIndex !== -1) {
                s =
                    s.slice(0, dotIndex + 1) +
                    s.slice(dotIndex + 1).replace(/\./g, '')
            }
            return sign + s
        },
        onCoordInput(field, event) {
            const el = event && event.target
            if (!el || (field !== 'longitude' && field !== 'latitude')) return
            const next = this.filterCoordInput(el.value)
            this[field] = next
            if (el.value !== next) {
                el.value = next
            }
        },
        applyEditCache(cached) {
            if (!cached) return Promise.resolve()
            this.form.buildingName = cached.buildingName || ''
            this.form.floor = cached.floor || ''
            this.form.name = cached.name || ''
            this.form.roomEquipt = cached.roomEquipt || ''
            this.form.deviceSn = cached.deviceSn || ''
            this.form.capacity =
                cached.capacity != null && cached.capacity !== ''
                    ? String(cached.capacity)
                    : ''
            this.uploadedCover = String(cached.pic || '').trim()
            this.applyCoordFromCache(
                cached.coord || { lng: cached.lng, lat: cached.lat }
            )
            if (this.form.buildingName) {
                return this.loadBuildingFloors(this.form.buildingName)
            }
            return Promise.resolve()
        },
        applyOtherAdminFromCache(cached) {
            if (!cached) return
            const raw = String(cached.otherAdmin || '').trim()
            if (!raw) {
                this.adminSlots = [{ label: '', value: '', phone: '', name: '' }]
                return
            }
            const phones = raw
                .split(/[;；]/)
                .map((phone) => String(phone || '').trim())
                .filter(Boolean)
            if (!phones.length) {
                this.adminSlots = [{ label: '', value: '', phone: '', name: '' }]
                return
            }
            this.adminSlots = phones.map((phone) => {
                const matched = this.adminOptions.find(
                    (opt) => String((opt && opt.phone) || '').trim() === phone
                )
                if (matched) {
                    return {
                        label: matched.label,
                        value: matched.value,
                        name: matched.name,
                        phone: matched.phone,
                    }
                }
                return {
                    label: phone,
                    value: phone,
                    name: '',
                    phone,
                }
            })
        },
        initPageFromRoute() {
            if (this.$route.name !== 'addMeetingRoom') return

            const id =
                this.$route.query.id != null && this.$route.query.id !== ''
                    ? String(this.$route.query.id)
                    : ''
            const routeKey = `add:${id}`
            if (routeKey === this.addRoomRouteKey) {
                return
            }
            this.addRoomRouteKey = routeKey

            this.resetFormState()
            const editCache = id ? this.readMeetingRoomEditCache(id) : null
            if (id) {
                this.roomId = id
            } else {
                sessionStorage.removeItem(MEETING_ROOM_EDIT_KEY)
            }
            this.loadOtherNormalAdmins().then(() => {
                this.applyOtherAdminFromCache(editCache)
            })
            this.loadBuildingNames().then(() => this.applyEditCache(editCache))
        },
        revokeLocalPreview() {
            if (this.localPreviewUrl && this.localPreviewUrl.startsWith('blob:')) {
                URL.revokeObjectURL(this.localPreviewUrl)
            }
            this.localPreviewUrl = ''
        },
        onUploadImageClick() {
            if (this.uploading) return
            const input = this.$refs.imageInput
            if (!input) return
            input.value = ''
            input.click()
        },
        onImageFileChange(event) {
            const file = event.target && event.target.files && event.target.files[0]
            if (!file) return
            if (!file.type || !file.type.startsWith('image/')) {
                this.$toast && this.$toast('请选择图片文件')
                return
            }

            this.revokeLocalPreview()
            this.localPreviewUrl = URL.createObjectURL(file)
            this.uploadImageFile(file)
        },
        parseUploadCover(uploadRes) {
            const uploaded = uploadRes && uploadRes.data
            if (typeof uploaded === 'string') return uploaded
            if (uploaded && typeof uploaded === 'object') {
                return uploaded.url || uploaded.path || uploaded.pic || ''
            }
            return ''
        },
        uploadImageFile(file) {
            if (!(file instanceof File)) {
                this.revokeLocalPreview()
                this.uploadedCover = ''
                this.$toast && this.$toast('请选择有效的图片文件')
                return
            }

            const extIndex = file.name.lastIndexOf('.')
            const ext = extIndex >= 0 ? file.name.slice(extIndex) : ''
            const renamedFile = new File([file], `${Date.now()}${ext}`, {
                type: file.type,
            })

            const formData = new FormData()
            formData.append('file', renamedFile)

            this.uploading = true
            this.uploadedCover = ''
            Toast.loading({
                message: '上传中...',
                forbidClick: true,
                duration: 0,
            })

            uploadFileRequest(formData)
                .then((res) => {
                    const cover = this.parseUploadCover(res)
                    if (!cover) {
                        this.revokeLocalPreview()
                        this.$toast && this.$toast('图片上传失败')
                        return
                    }
                    this.uploadedCover = cover
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    this.revokeLocalPreview()
                    const msg = (err && (err.message || err.msg)) || '图片上传失败'
                    this.$toast && this.$toast(msg)
                })
                .finally(() => {
                    this.uploading = false
                    Toast.clear()
                })
        },
        openProjectSitePicker() {
            if (!this.projectSiteOptions.length) {
                this.$toast && this.$toast('暂无可选项目名称')
                return
            }
            const idx = this.projectSiteOptions.findIndex(
                (item) =>
                    item.value === this.form.projectSiteId ||
                    item.label === this.form.buildingName
            )
            this.projectSitePickerIndex = idx >= 0 ? idx : 0
            this.projectSitePickerKey += 1
            this.showProjectSitePicker = true
        },
        onConfirmProjectSite(values) {
            const selected = Array.isArray(values) ? values[0] : values
            let nextBuildingName = ''
            if (selected && typeof selected === 'object') {
                nextBuildingName = selected.label || selected.value || ''
            } else if (selected) {
                nextBuildingName = String(selected)
            }
            nextBuildingName = nextBuildingName.trim()
            const changed = nextBuildingName !== this.form.buildingName
            this.form.buildingName = nextBuildingName
            this.form.projectSiteId = nextBuildingName
            this.showProjectSitePicker = false
            if (changed) {
                this.form.floorId = ''
                this.form.floor = ''
            }
            if (nextBuildingName) {
                this.loadBuildingFloors(nextBuildingName)
            } else {
                this.floorOptions = []
                this.syncFloorColumns()
            }
        },
        openFloorPicker() {
            if (!this.form.buildingName) {
                this.$toast && this.$toast('请先选择项目名称')
                return
            }
            if (!this.floorOptions.length) {
                this.$toast && this.$toast('暂无可选楼层')
                return
            }
            const idx = this.floorOptions.findIndex(
                (item) =>
                    item.value === this.form.floorId ||
                    item.label === this.form.floor
            )
            this.floorPickerIndex = idx >= 0 ? idx : 0
            this.floorPickerKey += 1
            this.showFloorPicker = true
        },
        onConfirmFloor(values) {
            const selected = Array.isArray(values) ? values[0] : values
            if (selected && typeof selected === 'object') {
                this.form.floor = selected.label
                this.form.floorId = selected.value
            } else if (selected) {
                this.form.floor = selected
                this.form.floorId = ''
            }
            this.showFloorPicker = false
        },
        handleSubmit() {
            if (this.submitting) return
            if (!this.form.buildingName) {
                this.$toast && this.$toast('请选择项目名称')
                return
            }
            if (!this.form.floor) {
                this.$toast && this.$toast('请选择所属楼层')
                return
            }
            if (!this.form.name) {
                this.$toast && this.$toast('请输入会议室名称')
                return
            }
            if (!this.uploadedCover) {
                this.$toast && this.$toast('请上传会议室图片')
                return
            }
            if (this.uploading) {
                this.$toast && this.$toast('图片上传中，请稍候')
                return
            }
            if (!this.form.capacity || Number(this.form.capacity) < 1) {
                this.$toast && this.$toast('请输入有效的容纳人数')
                return
            }

            this.submitting = true
            Toast.loading({
                message: '保存中...',
                forbidClick: true,
                duration: 0,
            })
            const lngStr = String(this.longitude || '').trim()
            const latStr = String(this.latitude || '').trim()
            const lng = parseFloat(lngStr)
            const lat = parseFloat(latStr)
            const otherAdmin = this.adminSlots
                .map((slot) => String((slot && slot.phone) || '').trim())
                .filter(Boolean)
                .join(';')
            const payload = {
                buildingName: this.form.buildingName,
                floor: this.form.floor,
                name: this.form.name,
                roomEquipt: this.form.roomEquipt,
                deviceSn: this.form.deviceSn,
                pic: this.uploadedCover,
                capacity: Number(this.form.capacity),
                otherAdmin,
            }
            if (this.isEditMode) {
                payload.id = parseInt(this.roomId)
                if (lngStr && latStr && !Number.isNaN(lng) && !Number.isNaN(lat)) {
                    payload.coord = { lng, lat }
                }
            } else {
                payload.coord = {
                    lng: Number.isNaN(lng) ? 0 : lng,
                    lat: Number.isNaN(lat) ? 0 : lat,
                }
            }

            saveMeetingRoomRequest(payload).then(() => {
                    this.$toast && this.$toast('保存成功')
                    this.$router.replace({ name: 'meetingRoomManage' })
                })
                .catch((err) => {
                    if (isUnauthorizedError(err)) return
                    const msg = (err && (err.message || err.msg)) || '保存失败'
                    this.$toast && this.$toast(msg)
                })
                .finally(() => {
                    this.submitting = false
                    Toast.clear()
                })
        },
    },
}
</script>

<style scoped>
.page {
    min-height: 100vh;
    background: #eef3f9;
    box-sizing: border-box;
    padding-bottom: calc(0.4rem + env(safe-area-inset-bottom));
}

.content {
    padding: 0.28rem 0.28rem 0.32rem;
    box-sizing: border-box;
}

.card {
    padding: 0.28rem 0.28rem 0.08rem;
    margin-bottom: 0.24rem;
    background: #ffffff;
    border-radius: 0.24rem;
    box-shadow: 0 0.06rem 0.2rem rgba(40, 90, 160, 0.06);
    box-sizing: border-box;
}

.card-title {
    margin-bottom: 0.2rem;
    font-size: 0.3rem;
    font-weight: 700;
    color: #1e3a5f;
}

.field-row--name {
    justify-content: center;
    gap: 0.16rem;
    min-height: 0.96rem;
    padding: 0.24rem 0;
    margin-bottom: 0;
    border-bottom: 1px solid #eef2f7;
    box-sizing: border-box;
}

.field-row--name .field-label {
    width: auto;
    flex-shrink: 0;
}

.field-row--name .field-input {
    flex: 0 1 auto;
    min-width: 3.8rem;
    max-width: 100%;
    text-align: right;
}

.card-title--image {
    margin-top: 0.2rem;
    margin-bottom: 0.2rem;
    padding-top: 0;
    border-top: none;
}

.card-title--coord {
    flex: 1;
    min-width: 0;
    margin-bottom: 0;
    font-weight: 600;
}

.coord-title-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.16rem;
    margin-bottom: 0.08rem;
}

.coord-fetch-btn {
    flex-shrink: 0;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    font-size: 0.22rem;
    line-height: 1.35;
    color: #2b7de9;
    cursor: pointer;
    white-space: nowrap;
}

.coord-fetch-btn:active {
    opacity: 0.75;
}

.admin-label-wrap {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    gap: 0.1rem;
}

.admin-label-wrap .field-label {
    width: auto;
}

.admin-remove-btn {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.36rem;
    height: 0.36rem;
    margin: 0;
    padding: 0;
    border: none;
    background: none;
    color: #ee0a24;
    cursor: pointer;
}

.admin-remove-btn svg {
    width: 100%;
    height: 100%;
    display: block;
}

.admin-remove-btn:active {
    opacity: 0.75;
}

.upload-section {
    padding-bottom: 0.24rem;
}

.upload-section--has-preview {
    padding-bottom: 0.2rem;
}

.preview-wrap {
    margin-bottom: 0;
}

.upload-section--has-preview .preview-wrap {
    margin-bottom: 0.08rem;
}

.preview-img {
    display: block;
    width: 100%;
    max-height: 3.6rem;
    object-fit: cover;
    border-radius: 0.16rem;
    background: #d9e6f5;
}

.upload-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 2rem;
    height: 0.72rem;
    padding: 0 0.32rem;
    border: 1px dashed #2b7de9;
    border-radius: 0.16rem;
    background: #f0f7ff;
    color: #2b7de9;
    font-size: 0.28rem;
    cursor: pointer;
}

.upload-btn:active {
    opacity: 0.88;
}

.upload-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.upload-tip {
    margin: 0.16rem 0 0;
    font-size: 0.22rem;
    color: #9aa8bc;
    line-height: 1.4;
}

.upload-tip--compact {
    margin: 0 0 0.16rem;
}

.upload-input-hidden {
    display: none;
}

.field-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    min-height: 0.96rem;
    border-bottom: 1px solid #eef2f7;
    box-sizing: border-box;
}

.card .field-row--name {
    border-bottom: 1px solid #eef2f7;
}

.card .field-row:last-child {
    border-bottom: none;
}

.field-row-input {
    align-items: center;
}

.field-label {
    flex-shrink: 0;
    width: 2.4rem;
    font-size: 0.28rem;
    color: #5a6f8a;
    line-height: 1.35;
}

.field-input {
    flex: 1;
    min-width: 0;
    border: none;
    outline: none;
    background: transparent;
    font-size: 0.28rem;
    color: #1e3a5f;
    text-align: right;
}

.field-input::placeholder {
    color: #b0bccb;
}

.field-row--picker {
    cursor: pointer;
}

.field-row--readonly {
    cursor: default;
}

.access-sn-block {
    border-bottom: 1px solid #eef2f7;
    padding-bottom: 0.12rem;
    box-sizing: border-box;
}

.field-row--access-sn {
    border-bottom: none;
    min-height: 0.88rem;
}

.access-sn-tip {
    margin: 0 0 0.06rem;
    text-align: right;
    font-size: 0.22rem;
    line-height: 1.35;
    color: #ee0a24;
    cursor: pointer;
}

.access-sn-tip:active {
    opacity: 0.75;
}

.field-value-wrap {
    flex: 1;
    min-width: 0;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 0.06rem;
}

.field-input--picker {
    flex: 1;
    min-width: 0;
    text-align: right;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.field-arrow {
    flex-shrink: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 0.28rem;
    height: 0.28rem;
    color: #b0bccb;
}

.field-arrow svg {
    width: 100%;
    height: 100%;
    display: block;
}

.field-input--picker.is-placeholder {
    color: #b0bccb;
}

.submit-btn {
    width: 100%;
    height: 0.96rem;
    margin-top: 0.08rem;
    border: none;
    border-radius: 0.48rem;
    background: #2b7de9;
    color: #ffffff;
    font-size: 0.32rem;
    font-weight: 600;
    cursor: pointer;
    box-shadow: 0 0.1rem 0.24rem rgba(43, 125, 233, 0.28);
}

.submit-btn:active {
    opacity: 0.92;
}

.submit-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.page ::v-deep .van-nav-bar {
    background: #ffffff;
}

.page ::v-deep .van-nav-bar .van-nav-bar__title {
    color: #1e3a5f;
    font-weight: 600;
}

.page ::v-deep .van-nav-bar .van-icon {
    color: #2b7de9;
}
</style>
